// 09:00 Europe/London HL dealing-cutoff countdown (HL's cutoff is 09:00 UK
// for every fund, whatever its valuation point — verified by paolo against
// the live HL account 2026-08-19). Extracted from GoldSignalPanel.vue /
// GgeSignalPanel.vue (option C, 2026-10-11).
//
// Rendered from the browser's wall clock; Europe/London is derived via
// Intl so BST/GMT is handled by the browser's tz database. Calendar-aware
// (BGA parity — useSignalPipeline.nextCutoffTarget): target today's cutoff
// only on an open day before 09:00, else the calendar's next day, else a
// weekend-skip fallback (calendar endpoint down / not yet loaded). With a
// `dealingCalendarKey` the fund's own dealing calendar wins (gold closes on
// days the UK does not — CalendarStatus.fundClosures / nextDealingDay).
import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { CalendarStatus } from '../api/client'

const HL_CUTOFF_LONDON_HOUR = 9

/** A Date whose UTC clock reads the London wall clock at `ms`. */
export function londonWallClock(ms: number): Date {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(ms))
  const map: Record<string, string> = {}
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value
  return new Date(Date.UTC(
    Number(map.year), Number(map.month) - 1, Number(map.day),
    Number(map.hour), Number(map.minute), Number(map.second)))
}

export function nextHlCutoffLondon(now: Date, cal: CalendarStatus | null, dealingCalendarKey?: string): Date {
  const todayCutoff = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), HL_CUTOFF_LONDON_HOUR, 0, 0))
  const closedForFund = dealingCalendarKey ? !!cal?.fundClosures?.[dealingCalendarKey] : false
  const openToday = !!cal?.tradingDay && !closedForFund
  if (openToday && todayCutoff.getTime() > now.getTime()) return todayCutoff
  const nextDay = (dealingCalendarKey ? cal?.nextDealingDay?.[dealingCalendarKey] : undefined) ?? cal?.nextTradingDay
  if (nextDay) {
    const [y, mo, d] = nextDay.split('-').map(Number)
    return new Date(Date.UTC(y, mo - 1, d, HL_CUTOFF_LONDON_HOUR, 0, 0))
  }
  const t = new Date(todayCutoff)
  if (t.getTime() <= now.getTime()) t.setUTCDate(t.getUTCDate() + 1)
  while (t.getUTCDay() === 0 || t.getUTCDay() === 6) t.setUTCDate(t.getUTCDate() + 1)
  return t
}

export function useHlCutoffCountdown(calendar: Ref<CalendarStatus | null>, dealingCalendarKey?: string): {
  countdown: ComputedRef<{ label: string; passed: boolean }>
  start: () => void
  stop: () => void
} {
  const nowTick = ref(Date.now())
  let handle: number | null = null

  const countdown = computed(() => {
    const now = londonWallClock(nowTick.value)
    const diffMs = nextHlCutoffLondon(now, calendar.value, dealingCalendarKey).getTime() - now.getTime()
    // Target is always the next valid cutoff, so "passed" is at most a
    // transient single-tick state (no sticky "passed").
    if (diffMs <= 0) return { label: 'HL cutoff passed', passed: true }
    const totalMinutes = Math.floor(diffMs / 60_000)
    const d = Math.floor(totalMinutes / 1_440)
    const h = Math.floor((totalMinutes % 1_440) / 60)
    const m = totalMinutes % 60
    const label = d > 0
      ? `${d}d ${h}h ${m}m to HL cutoff`
      : h > 0 ? `${h}h ${m}m to HL cutoff` : `${m}m to HL cutoff`
    return { label, passed: false }
  })

  // 1s refresh: "how many minutes left" precision, not sub-second.
  const start = () => { if (handle === null) handle = window.setInterval(() => { nowTick.value = Date.now() }, 1_000) }
  const stop = () => { if (handle !== null) { window.clearInterval(handle); handle = null } }

  return { countdown, start, stop }
}
